import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack,
  Business,
  Language,
  Assessment,
  People,
  CheckCircle,
  AdminPanelSettings,
  Email,
  Person,
  Search
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { useTheme } from '@mui/material/styles';
import { useAppTheme } from '@contexts/ThemeContext';
import { useOrgAdminStore } from '@store/orgAdminStore';

const ViewOrgAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();

  const {
    orgDetails,
    loading,
    error,
    fetchOrgAdminDetails,
  } = useOrgAdminStore();

  // Pagination state for users table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (id) {
      fetchOrgAdminDetails(id);
    }
  }, [id, fetchOrgAdminDetails]);

  // Filter users based on search term (name or email)
  const filteredUsers = orgDetails?.users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  }) || [];

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset to first page when search changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error || !orgDetails) {
    return (
      <Layout>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Alert severity="error">{error || 'Organization not found'}</Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ width: '100%', height: '100%', p: 2 }}>
        {/* Back Button */}
        <Box mb={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            variant="text"
            sx={{ color: 'text.secondary' }}
          >
            Back
          </Button>
        </Box>

        {/* Page Title */}
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <AdminPanelSettings
            sx={{
              fontSize: 36,
              color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main
            }}
          />
          <Typography variant="h4" fontWeight={600}>
            Organization Details
          </Typography>
        </Box>

        {/* Organization Info Card */}
        <Card elevation={3} sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box>
              {/* Organization Name Section */}
              <Box mb={3}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 600, letterSpacing: 1 }}
                >
                  Organization Name
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mt={1}>
                  <Business
                    sx={{
                      fontSize: 32,
                      color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main
                    }}
                  />
                  <Typography variant="h5" fontWeight={600}>
                    {orgDetails.organization_name}
                  </Typography>
                  <Chip
                    label={orgDetails.status}
                    color={orgDetails.status === 'active' ? 'success' : 'default'}
                    size="medium"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2.5 }} />

              {/* Managed By Section */}
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 600, letterSpacing: 1 }}
                >
                  Managed By
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.5} mt={1.5}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Person
                      sx={{
                        fontSize: 24,
                        color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main
                      }}
                    />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                        Administrator Name
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {orgDetails.user_name}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Email
                      sx={{
                        fontSize: 24,
                        color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main
                      }}
                    />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                        Email Address
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {orgDetails.user_email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 3
          }}
        >
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Language sx={{ fontSize: 40, color: theme.palette.info.main }} />
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {orgDetails.stats.total_domains}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Domains
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Assessment sx={{ fontSize: 40, color: theme.palette.warning.main }} />
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {orgDetails.stats.total_assessments}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assessments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <People sx={{ fontSize: 40, color: theme.palette.success.main }} />
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {orgDetails.stats.total_users}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Domains Section */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Language sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight={600}>
                Allowed Domains ({orgDetails.domains.length})
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {orgDetails.domains.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {orgDetails.domains.map((domain, idx) => (
                  <Chip
                    key={idx}
                    label={domain.domain}
                    color="info"
                    variant="filled"
                    icon={<CheckCircle />}
                    sx={{
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: 1.5
                      }
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No domains assigned
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Assessments Section */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Assessment sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight={600}>
                Assigned Assessments ({orgDetails.assessments.length})
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {orgDetails.assessments.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Assessment Name</strong></TableCell>
                      <TableCell><strong>Created Date</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orgDetails.assessments.map((assessment) => (
                      <TableRow key={assessment.id}>
                        <TableCell>{assessment.title}</TableCell>
                        <TableCell>{new Date(assessment.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No assessments assigned to this organization
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Users Section */}
        <Card elevation={2}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <People sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight={600}>
                Users with These Domains ({orgDetails.users.length})
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Search Bar */}
            <Box mb={2}>
              <TextField
                fullWidth
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={handleSearchChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {orgDetails.users.length > 0 ? (
              filteredUsers.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Name</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Role</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredUsers
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((user) => (
                            <TableRow key={user.userId}>
                              <TableCell>{user.name}</TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>
                                <Chip
                                  label={user.rollId === 1 ? 'Admin' : 'User'}
                                  size="small"
                                  color={user.rollId === 1 ? 'primary' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredUsers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                  No users found matching "{searchTerm}"
                </Typography>
              )
            ) : (
              <Typography variant="body2" color="text.secondary">
                No users found with these domains
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default ViewOrgAdmin;
